import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { listVideos, Video } from "../api";
import { formatBytes, relativeTime } from "../utils";

type AvatarBadgeKind = "none" | "queued" | "running" | "partial" | "done";

interface AvatarBadgePreview {
  kind: AvatarBadgeKind;
  done: number;
  total: number;
}

// Preview-only: deterministic mock based on video_id so the screen looks
// realistic without any backend wiring. Will be replaced by a real
// aggregate read once batch sessions land on the backend.
function mockAvatarBadge(videoId: string): AvatarBadgePreview {
  const seed = videoId.charCodeAt(0) + videoId.charCodeAt(videoId.length - 1);
  const pick = seed % 5;
  if (pick === 0) return { kind: "none", done: 0, total: 0 };
  if (pick === 1) return { kind: "queued", done: 0, total: 4 };
  if (pick === 2) return { kind: "running", done: 1, total: 6 };
  if (pick === 3) return { kind: "partial", done: 3, total: 6 };
  return { kind: "done", done: 6, total: 6 };
}

function AvatarBadge({ preview }: { preview: AvatarBadgePreview }) {
  if (preview.kind === "none") return null;
  const map: Record<AvatarBadgeKind, { label: string; cls: string; dot?: string }> = {
    none: { label: "", cls: "" },
    queued: {
      label: "en attente",
      cls: "bg-stone/40 text-charcoal border-stone",
    },
    running: {
      label: "en cours",
      cls: "bg-lemonade/30 text-charcoal border-lemonade",
      dot: "animate-pulse bg-charcoal",
    },
    partial: {
      label: "partiel",
      cls: "bg-ivory-tint text-charcoal border-stone",
    },
    done: {
      label: "prêt",
      cls: "bg-charcoal text-ivory border-charcoal",
    },
  };
  const m = map[preview.kind];
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded border text-[10.5px] font-mono uppercase tracking-wide ${m.cls}`}
      title={`${preview.done}/${preview.total} avatars · ${m.label}`}
    >
      {m.dot && <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />}
      avatars {preview.done}/{preview.total}
    </span>
  );
}

export default function AdminVideos() {
  const nav = useNavigate();
  const [videos, setVideos] = useState<Video[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [titleFilter, setTitleFilter] = useState<string>("");

  useEffect(() => {
    listVideos()
      .then(setVideos)
      .catch((e) => {
        if (String(e.message).includes("401")) nav("/admin/login");
        else setErr(e.message);
      });
  }, [nav]);

  // Unique titles present in the result set → populate the filter dropdown
  const titles = useMemo(() => {
    if (!videos) return [] as string[];
    const s = new Set<string>();
    for (const v of videos) if (v.link_title) s.add(v.link_title);
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [videos]);

  const filtered = useMemo(() => {
    if (!videos) return null;
    if (!titleFilter) return videos;
    return videos.filter((v) => v.link_title === titleFilter);
  }, [videos, titleFilter]);

  const openAvatarBuilder = (videoId: string) => {
    // Reuses the existing builder, pre-filled with this video as motion
    // source. The multi-select UI lives inside AvatarBuilderPanel.
    nav(`/admin?app=avatar&reuseVideoId=${encodeURIComponent(videoId)}`);
  };

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-2xl font-bold">Videos received</h2>
        {videos && (
          <span className="text-sm text-concrete">
            {filtered?.length} / {videos.length}
          </span>
        )}
      </div>

      {videos && videos.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-sm text-concrete">Filter by link:</label>
          <select
            value={titleFilter}
            onChange={(e) => setTitleFilter(e.target.value)}
            className="px-3 py-1.5 bg-white border border-stone rounded-lg text-sm focus:outline-none focus:border-charcoal"
          >
            <option value="">All ({videos.length})</option>
            {titles.map((t) => {
              const count = videos.filter((v) => v.link_title === t).length;
              return (
                <option key={t} value={t}>
                  {t} ({count})
                </option>
              );
            })}
          </select>
          {titleFilter && (
            <button
              onClick={() => setTitleFilter("")}
              className="text-xs text-concrete underline hover:text-charcoal"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {err && <div className="text-red-600">{err}</div>}
      {!videos && !err && <div className="text-concrete">Loading…</div>}

      {videos && videos.length === 0 && (
        <div className="text-concrete py-12 text-center bg-white rounded-xl">
          No videos yet. Create a link in the <b>New link</b> tab.
        </div>
      )}

      {filtered && filtered.length === 0 && videos && videos.length > 0 && (
        <div className="text-concrete py-8 text-center bg-white rounded-xl">
          No videos match <b>{titleFilter}</b>.
        </div>
      )}

      {filtered && filtered.length > 0 && (
        <ul className="divide-y divide-stone bg-white rounded-xl shadow-sm">
          {filtered.map((v) => {
            const badge = mockAvatarBadge(v.video_id);
            return (
              <li key={v.video_id} className="p-4 hover:bg-ivory transition">
                <div className="flex justify-between items-start gap-3">
                  <Link
                    to={`/admin/videos/${v.video_id}`}
                    className="min-w-0 flex-1 block"
                  >
                    <div className="font-bold text-base truncate">
                      {v.link_title || "Untitled"}
                    </div>
                    <div className="text-sm text-concrete mt-0.5">
                      {relativeTime(v.uploaded_at)}
                      {" · "}
                      <span className="font-mono text-xs">
                        {new Date(v.uploaded_at).toLocaleString()}
                      </span>
                      {" · "}
                      {formatBytes(v.size_bytes)}
                    </div>
                    {v.recorder_name && (
                      <div className="text-xs text-concrete/80 mt-0.5">
                        from {v.recorder_name}
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span className="text-xs text-concrete uppercase tracking-wider font-mono">
                        {v.status}
                      </span>
                      <AvatarBadge preview={badge} />
                    </div>
                  </Link>
                  <div className="shrink-0">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openAvatarBuilder(v.video_id);
                      }}
                      className="px-3 py-1.5 rounded-lg border border-charcoal text-charcoal text-xs font-mono uppercase tracking-wider hover:bg-charcoal hover:text-ivory transition"
                      title="Lancer le builder avatar avec cette vidéo comme mouvement"
                    >
                      Générer avatar
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {filtered && filtered.length > 0 && (
        <p className="text-xs text-concrete font-mono">
          Preview UI · les badges « avatars X/Y » sont actuellement simulés (mock).
        </p>
      )}
    </section>
  );
}
